import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import type { AppModel } from "../../../app/app-model";

import { apiPostAuthTokenLogin } from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { genericErrorMsg } from "../../../app/common-messages";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { Spinner } from "../../../components/ui-kit/spinner";
import { EnrollmentOffer } from "./enrollment-offer";
import {
  LoginChallenge,
  LoginNext,
  loginNextLayout,
  nextAfterLogin,
} from "./login-challenge";

type TokenLoginView =
  | { kind: "loading" }
  | { kind: "failed"; reason: FailureReason }
  | LoginNext;

/** Why a login link didn't work. Worded at render, not where it's decided. */
type FailureReason = "incompleteLink" | "deadLink" | "unexpected";

const layoutClasses: Record<TokenLoginView["kind"], string> = {
  loading: "w-80 items-center",
  failed: "w-80 items-center",
  ...loginNextLayout,
};

export function TokenLoginRoute({ app }: { app: AppModel }) {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState<TokenLoginView>({ kind: "loading" });
  const api = app.seerlinqApi;

  useEffect(() => {
    const token = searchParams.get("token");
    const expiryToken = searchParams.get("expiry_token");

    if (!token || !expiryToken) {
      setView({ kind: "failed", reason: "incompleteLink" });
      return;
    }

    void apiPostAuthTokenLogin({
      body: { token, expiry_token: expiryToken },
      meta: { rotateOn401: "never" },
    })
      .then(({ data }) => {
        // A login link is only the first factor — MFA gates it exactly as it
        // gates a password login, so the link alone may not end in a session.
        const next = nextAfterLogin(data);

        if (next) {
          setView(next);
        } else {
          api.completeLogin();
        }
      })
      .catch((error) => {
        if (error instanceof RequestError) {
          if (error.response.status === 406) {
            setView({ kind: "failed", reason: "deadLink" });
          } else {
            setView({ kind: "failed", reason: "unexpected" });
          }
        } else {
          throw error;
        }
      });
  }, [api, searchParams]);

  function failureMessage(reason: FailureReason) {
    switch (reason) {
      case "incompleteLink":
        return t`Invalid login link. Missing token parameters.`;

      case "deadLink":
        return t`This login link is invalid, expired, or has already been used.`;

      case "unexpected":
        return t(genericErrorMsg);
    }
  }

  return (
    <div className="grow flex items-center justify-center">
      <div className={`flex flex-col ${layoutClasses[view.kind]}`}>
        {view.kind === "loading" && (
          <>
            <Spinner size={32} />
            <p className="text-muted-foreground mt-4 text-sm">
              <Trans>Logging in...</Trans>
            </p>
          </>
        )}

        {view.kind === "failed" && (
          <>
            <ErrorMessage message={failureMessage(view.reason)} />
            <div className="mt-6">
              <Link
                to="/login"
                className="text-xs text-brand-primary hover:underline"
              >
                <Trans>Back to login</Trans>
              </Link>
            </div>
          </>
        )}

        {(view.kind === "enrollment" || view.kind === "verification") && (
          <LoginChallenge
            api={api}
            challenge={view}
            onCancel={() => {
              void navigate("/login");
            }}
          />
        )}

        {view.kind === "enrollmentOffer" && <EnrollmentOffer api={api} />}
      </div>
    </div>
  );
}
